import { Box, Tooltip } from "@mui/material";

import { useTranslation } from "react-i18next";
import { common, reduce } from "../../../../../i18n";
import CustomEcoEfficiencyChart from "../economicEfficiency/CustomEcoEfficiencyChart";
import { BarSeriesType } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { EcoEfficiencyByComponentDataGrid } from "./ecoEfficiencyByComponent.type";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";

export const EcoEfficiencyByComponent = () => {
  const { t, i18n } = useTranslation([common, reduce], { nsMode: "fallback" });

  const { indicators, yearly, selectedEcoEfficiency } =
    EcoEfficiencyDashboardContext();

  const [series, setSeries] = useState<MakeOptional<BarSeriesType, "type">[]>(
    [],
  );

  const [labels, setLabels] = useState<string[]>([]);

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Normalization"),
      children: [
        { field: `normalizationBaseYear` },
        { field: `normalizationPlan` },
        { field: `normalizationActual` },
      ],
      headerAlign: "center",
    },
    {
      groupId: t("Value"),
      children: [
        { field: `valueBaseYear` },
        { field: `valuePlan` },
        { field: `valueActual` },
      ],
      headerAlign: "center",
    },
  ];

  const [rows, setRows] = useState<EcoEfficiencyByComponentDataGrid[]>([]);

  const columns: GridColDef<EcoEfficiencyByComponentDataGrid>[] = [
    {
      field: "id",
      headerName: t("No."),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.row._id !== "ghg-emissions" && params.row._id !== "eco-f"
          ? `${params.id}`
          : ``;
      },
    },
    {
      field: "name",
      headerName: t("Name"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1.5,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "normalizationBaseYear",
      headerName: t("Base-Year"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value, row) =>
        row._id === "eco-f"
          ? `${NumberFormatterUtils.numberFormat(value, 4) ?? 0}`
          : `${NumberFormatterUtils.numberFormat(value) ?? 0}`,
    },
    // {
    //   field: "normalizationPlan",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   align: "right",
    //   minWidth: 120,
    //   valueFormatter: (value, row) =>
    //     row._id === "eco-f"
    //       ? `${NumberFormatterUtils.numberFormat(value, 4) ?? 0}`
    //       : `${NumberFormatterUtils.numberFormat(value) ?? 0}`,
    // },
    {
      field: "normalizationActual",
      headerName: t("Actual"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value, row) =>
        row._id === "eco-f"
          ? `${NumberFormatterUtils.numberFormat(value, 4) ?? 0}`
          : `${NumberFormatterUtils.numberFormat(value) ?? 0}`,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box color={"var( --status-waiting-leased-in-text)"}>
              {params.formattedValue}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "valueBaseYear",
      headerName: t("Base-Year"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box>{params.row._id !== "eco-f" ? params.formattedValue : ""}</Box>
          </Tooltip>
        );
      },
    },
    // {
    //   field: "valuePlan",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   align: "right",
    //   minWidth: 120,
    //   valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    //   renderCell: (params) => {
    //     return (
    //       <Tooltip title={params.formattedValue}>
    //         <Box>{params.row._id !== "eco-f" ? params.formattedValue : ""}</Box>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      field: "valueActual",
      headerName: t("Actual"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box color={"var( --status-waiting-leased-in-text)"}>
              {params.row._id !== "eco-f" ? params.formattedValue : ""}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "unit",
      headerName: t("Unit"),
      headerAlign: "left",
      align: "left",
      minWidth: 140,
      flex: 1,
    },
  ];

  const buildTable = () => {
    if (yearly) {
      const temp: EcoEfficiencyByComponentDataGrid[] = [
        ...yearly.components.map((c, index) => {
          const indicator = indicators.find((i) =>
            i.components.find((j) => j._id === c.economiccomponentsRef),
          );

          const component = indicator?.components.find(
            (i) => i._id === c.economiccomponentsRef,
          );

          return {
            _id: component?._id ?? "",
            id: `${index + 1}`,
            nameEn: component?.nameEn ?? "",
            nameTh: component?.nameTh ?? "",
            normalizationActual: c.componentValueNormalized,
            normalizationBaseYear: c.componentValueBaseYearNormalized,
            normalizationPlan: c.componentValuePlanNormalized,
            valueActual: c.componentValue,
            valueBaseYear: c.componentValueBaseYear,
            valuePlan: c.componentValuePlan,
            unit: component?.unit ?? "",
          };
        }),
        {
          _id: "ghg-emissions",
          id: "ghg-emissions",
          nameEn: `${t("GHG-Emissions")}`,
          nameTh: `${t("GHG-Emissions")}`,
          normalizationActual: yearly.environmentImpactNormalized,
          normalizationBaseYear: yearly.environmentImpactBaseYearNormalized,
          normalizationPlan: yearly.environmentImpactPlanNormalized,
          valueActual: yearly.environmentImpact,
          valueBaseYear: yearly.environmentImpactBaseYear,
          valuePlan: yearly.environmentImpactPlan,
          unit: "kgCO₂e",
        },
        {
          _id: "eco-f",
          id: "eco-f",
          nameEn: `${t("Eco-Efficiency")}`,
          nameTh: `${t("Eco-Efficiency")}`,
          normalizationActual: yearly.ecoEfficiencyNormalized,
          normalizationBaseYear: yearly.ecoEfficiencyBaseYearNormalized,
          normalizationPlan: yearly.ecoEfficiencyPlanNormalized,
          valueActual: NaN,
          valueBaseYear: NaN,
          valuePlan: NaN,
          unit: "",
        },
      ];

      setRows(temp);
    }
  };

  useEffect(() => {
    if (indicators.length > 0 && yearly) {
      const result = yearly;

      setSeries([
        // {
        //   data: [
        //     ...result.components.map((c) => c.componentValuePlanNormalized),
        //     result.environmentImpactPlanNormalized,
        //     result.ecoEfficiencyPlanNormalized,
        //   ],
        //   color: "#10B981",
        //   label: `${t("Plan")}`,
        // },
        {
          data: [
            ...result.components.map((c) => c.componentValueNormalized),
            result.environmentImpactNormalized,
            result.ecoEfficiencyNormalized,
          ],
          color: "#2563EB",
          label: `${t("Actual")}`,
        },
      ]);

      setLabels([
        ...result.components.map((c) => {
          const indicator = indicators.find((i) =>
            i.components.find((j) => j._id === c.economiccomponentsRef),
          );

          const component = indicator?.components.find(
            (i) => i._id === c.economiccomponentsRef,
          );

          return i18n.language === "th" ? component!.nameTh : component!.nameEn;
        }),
        t("GHG-Emissions"),
        t("Eco-Efficiency"),
      ]);

      buildTable();
    }
  }, [yearly, indicators, t]);

  return (
    <>
      <CustomEcoEfficiencyChart
        series={series ?? []}
        labels={labels ?? []}
        header={
          !selectedEcoEfficiency
            ? t("Eco-Efficiency-By-Component")
            : `${t("Eco-Efficiency-By-Component")} (${dayjs(
                selectedEcoEfficiency!.startDate,
              )
                .locale(i18n.language)
                .format("MMM YYYY")} - ${dayjs(selectedEcoEfficiency!.endDate)
                .locale(i18n.language)
                .format("MMM YYYY")})`
        }
        info={t("Eco-Efficiency-By-Component-Description")}
        year={2024}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroup}
      />
    </>
  );
};
